import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import StatusBadge from '@/components/shared/StatusBadge';
import Modal from '@/components/shared/Modal';
import LeadForm from '@/components/internal/LeadForm';
import { useLeads, LEAD_STATUSES } from '@/hooks/useLeads';
import { formatDate, formatDateTime, formatConsumption, formatCurrency } from '@/utils/formatters';
import {
  FiEdit2, FiTrash2, FiArrowLeft, FiPhone, FiMail, FiMapPin,
  FiFileText, FiZap, FiCalendar, FiUser, FiClock, FiChevronRight,
  FiUpload, FiDollarSign, FiAlertCircle, FiRefreshCw
} from 'react-icons/fi';

/**
 * Felder, die das Backend (PUT /api/leads/:id) tatsaechlich speichert.
 * Firmen-/Kontaktdaten und `source` gehoeren NICHT dazu — dafuer fehlt noch
 * eine Schnittstelle. Aenderungen daran werden dem Nutzer offen gemeldet,
 * statt sie so darzustellen, als waeren sie gespeichert.
 */
const EDITABLE_LEAD_FIELDS = ['priority', 'notes', 'next_action', 'next_action_date'];

const COMPANY_LABELS = {
  name: 'Firmenname', street: 'Strasse', plz: 'PLZ', city: 'Stadt', industry: 'Branche',
  annual_consumption_kwh: 'Jahresverbrauch', current_price_ct_kwh: 'Aktueller Preis',
  current_supplier: 'Aktueller Versorger',
};
const CONTACT_LABELS = {
  first_name: 'Vorname', last_name: 'Nachname', email: 'E-Mail',
  phone: 'Telefon', position: 'Position',
};

const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';

/** Werte vergleichen; Zahlen aus der DB kommen als String ("12.5000"). */
const sameValue = (a, b) => {
  if (isBlank(a) || isBlank(b)) return isBlank(a) && isBlank(b);
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  return String(a) === String(b);
};

const changedLabels = (before, after, labels) =>
  Object.keys(labels)
    .filter(key => !sameValue((before || {})[key], (after || {})[key]))
    .map(key => labels[key]);

/** DATE-Spalten liefert der Server als ISO-Zeitstempel — das <input type="date"> braucht YYYY-MM-DD. */
const toDateInput = (value) => {
  if (!value) return '';
  const raw = String(value);
  const plain = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (plain) return raw;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** null/undefined zu '' — LeadForm ruft .trim() auf und React braucht kontrollierte Werte. */
const toFormText = (obj, keys) => {
  const out = {};
  keys.forEach(key => { out[key] = obj?.[key] ?? ''; });
  return out;
};

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // loadError = der Lead konnte nicht geladen werden (nichts anzuzeigen).
  // writeError = eine Aenderung schlug fehl — der geladene Lead bleibt gueltig
  // und sichtbar, der Fehler erscheint nur als Hinweis.
  const {
    getLeadById, updateLead, updateLeadStatus, deleteLead,
    loading, loadError, writeError, clearWriteError, reload,
  } = useLeads();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('uebersicht');
  const [actionError, setActionError] = useState('');
  const [editError, setEditError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState('');

  // Server-IDs sind Zahlen, useParams liefert Strings — getLeadById vergleicht als String.
  const lead = useMemo(() => getLeadById(id), [id, getLeadById]);

  // Formulardaten fuer den Bearbeiten-Dialog aufbereiten (Datum + null-Werte).
  const editInitialData = useMemo(() => {
    if (!lead) return null;
    return {
      company: toFormText(lead.company, Object.keys(COMPANY_LABELS)),
      contact: toFormText(lead.contact, Object.keys(CONTACT_LABELS)),
      source: lead.source || 'Kaltakquise',
      priority: lead.priority || 'normal',
      notes: lead.notes || '',
      next_action: lead.next_action || '',
      next_action_date: toDateInput(lead.next_action_date),
    };
  }, [lead]);

  if (loading) {
    return <p className="text-sm text-gray-400 py-20 text-center">Lade Lead …</p>;
  }

  // Ganzseiten-Fehler NUR beim Laden — ein fehlgeschlagener Schreibvorgang
  // darf die vollstaendig geladene Detailseite niemals ersetzen.
  if (loadError) {
    return (
      <div className="text-center py-20">
        <FiAlertCircle className="mx-auto text-red-400 mb-4" size={48} />
        <h2 className="text-xl font-semibold text-gray-900">Lead konnte nicht geladen werden</h2>
        <p className="text-sm text-red-600 mt-1">{loadError}</p>
        <div className="flex items-center justify-center space-x-3 mt-4">
          <Button variant="primary" icon={FiRefreshCw} onClick={reload}>Erneut versuchen</Button>
          <Button variant="secondary" onClick={() => navigate('/app/leads')}>Zurueck zur Liste</Button>
        </div>
      </div>
    );
  }

  // Waehrend des Loeschens ist der Lead durch das optimistische Update schon aus
  // der Liste verschwunden — ohne diesen Zwischenschritt saehe der Nutzer
  // "Lead nicht gefunden", bevor der Server ueberhaupt geantwortet hat.
  if (!lead && deleting) {
    return (
      <div className="text-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4" />
        <p className="text-gray-600">Lead wird geloescht …</p>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-20">
        <FiAlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
        <h2 className="text-xl font-semibold text-gray-900">Lead nicht gefunden</h2>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/app/leads')}>
          Zurueck zur Liste
        </Button>
      </div>
    );
  }

  const company = lead.company || {};
  const contact = lead.contact || {};

  // Lokal abgefangener Fehler hat Vorrang; writeError faengt Pfade ab, die hier
  // nicht eigens behandelt werden — so geht kein Schreibfehler verloren.
  const bannerError = actionError || writeError;
  const dismissBannerError = () => {
    setActionError('');
    clearWriteError();
  };

  const handleStatusChange = async (newStatus) => {
    setActionError('');
    setNotice('');
    try {
      await updateLeadStatus(lead.id, newStatus);
    } catch (err) {
      setActionError(err.message || 'Status konnte nicht gespeichert werden');
    }
  };

  /** Dialog schliessen, aber einen offenen Fehler in das Seitenbanner uebernehmen. */
  const closeEditModal = () => {
    setShowEditModal(false);
    if (editError) setActionError(editError);
    setEditError('');
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setShowDeleteConfirm(false);
    if (deleteError) setActionError(deleteError);
    setDeleteError('');
  };

  const handleEdit = async (data) => {
    setActionError('');
    setEditError('');
    setNotice('');

    // Was das Backend nicht speichern kann, ehrlich benennen.
    const blocked = [
      ...changedLabels(company, data.company, COMPANY_LABELS),
      ...changedLabels(contact, data.contact, CONTACT_LABELS),
      ...(sameValue(lead.source, data.source) ? [] : ['Quelle']),
    ];

    const changes = {};
    EDITABLE_LEAD_FIELDS.forEach(key => {
      const before = key === 'next_action_date' ? toDateInput(lead[key]) : lead[key];
      if (!sameValue(before, data[key])) changes[key] = data[key];
    });

    try {
      if (Object.keys(changes).length > 0) {
        await updateLead(lead.id, changes);
      }
      setShowEditModal(false);
      if (blocked.length > 0) {
        setNotice(
          `Nicht gespeichert: ${blocked.join(', ')}. Firmen- und Kontaktdaten koennen ` +
          'derzeit nur beim Anlegen eines Leads gesetzt werden.'
        );
      }
    } catch (err) {
      // Der Dialog bleibt offen — der Fehler gehoert sichtbar dorthin,
      // ein Banner dahinter waere vom Overlay verdeckt.
      setEditError(err.message || 'Aenderungen konnten nicht gespeichert werden');
      clearWriteError();
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setActionError('');
    setDeleteError('');
    setDeleting(true);
    try {
      await deleteLead(lead.id);
      navigate('/app/leads');
    } catch (err) {
      // Der Server meldet z.B. bei Angeboten am Lead HTTP 409 mit einer
      // verstaendlichen Begruendung — genau die zeigen wir im Dialog an.
      setDeleteError(err.message || 'Lead konnte nicht geloescht werden');
      clearWriteError();
      setDeleting(false);
    }
  };

  const tabs = [
    { key: 'uebersicht', label: 'Uebersicht' },
    { key: 'notizen', label: 'Notizen & Aktionen' },
    { key: 'angebote', label: 'Angebote' },
  ];

  const currentStatusIdx = LEAD_STATUSES.findIndex(s => s.value === lead.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <button
            onClick={() => navigate('/app/leads')}
            className="mt-1 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
            <div className="flex items-center space-x-3 mt-1">
              <StatusBadge status={lead.status} />
              <StatusBadge status={lead.priority} />
              {lead.source && <span className="text-sm text-gray-500">{lead.source}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="secondary" icon={FiEdit2} onClick={() => { setActionError(''); setEditError(''); setNotice(''); clearWriteError(); setShowEditModal(true); }}>
            Bearbeiten
          </Button>
          <Button variant="danger" icon={FiTrash2} onClick={() => { setDeleteError(''); setShowDeleteConfirm(true); }}>
            Loeschen
          </Button>
          <Button
            variant="primary"
            icon={FiZap}
            onClick={() => navigate(`/app/kalkulator/lead/${lead.id}`)}
          >
            Angebot kalkulieren
          </Button>
        </div>
      </div>

      {/* Fehler aus Schreibvorgaengen — der Lead selbst bleibt sichtbar */}
      {bannerError && (
        <div className="flex items-start space-x-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <FiAlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">Aenderung wurde nicht gespeichert</p>
            <p className="text-sm text-red-700/90 mt-0.5">{bannerError}</p>
          </div>
          <button onClick={dismissBannerError} className="text-xs font-semibold text-red-700 hover:underline">
            Schliessen
          </button>
        </div>
      )}

      {/* Hinweis auf nicht speicherbare Felder */}
      {notice && (
        <div className="flex items-start space-x-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <FiAlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
          <p className="flex-1 text-sm text-amber-800">{notice}</p>
          <button onClick={() => setNotice('')} className="text-xs font-semibold text-amber-800 hover:underline">
            Schliessen
          </button>
        </div>
      )}

      {/* Status-Pipeline */}
      <Card className="!p-4">
        <div className="flex items-center space-x-1 overflow-x-auto">
          {LEAD_STATUSES.filter(s => s.value !== 'verloren').map((status, idx) => {
            const isActive = status.value === lead.status;
            const isPast = idx < currentStatusIdx;
            return (
              <button
                key={status.value}
                onClick={() => handleStatusChange(status.value)}
                className={`flex items-center px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive ? 'bg-primary-600 text-white' :
                  isPast ? 'bg-primary-100 text-primary-700' :
                  'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {status.label}
                {idx < LEAD_STATUSES.length - 2 && (
                  <FiChevronRight className="ml-1 opacity-50" size={12} />
                )}
              </button>
            );
          })}
          <div className="border-l border-gray-200 ml-2 pl-2">
            <button
              onClick={() => handleStatusChange('verloren')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium ${
                lead.status === 'verloren' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-red-100 hover:text-red-700'
              }`}
            >
              Verloren
            </button>
          </div>
        </div>
      </Card>

      {/* Hauptinhalt */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Linke Spalte: Kontaktdaten */}
        <div className="space-y-4">
          <Card>
            <Card.Title>Ansprechpartner</Card.Title>
            <div className="mt-3 space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <FiUser className="text-primary-600" size={18} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{contact.first_name} {contact.last_name}</p>
                  {contact.position && <p className="text-xs text-gray-500">{contact.position}</p>}
                </div>
              </div>
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="flex items-center space-x-2 text-sm text-gray-600 hover:text-primary-600">
                  <FiMail size={14} />
                  <span>{contact.email}</span>
                </a>
              )}
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="flex items-center space-x-2 text-sm text-gray-600 hover:text-primary-600">
                  <FiPhone size={14} />
                  <span>{contact.phone}</span>
                </a>
              )}
            </div>
          </Card>

          <Card>
            <Card.Title>Firmendaten</Card.Title>
            <div className="mt-3 space-y-2 text-sm">
              {company.street && (
                <div className="flex items-start space-x-2 text-gray-600">
                  <FiMapPin size={14} className="mt-0.5" />
                  <span>{company.street}, {company.plz} {company.city}</span>
                </div>
              )}
              {company.industry && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Branche</span>
                  <span className="font-medium">{company.industry}</span>
                </div>
              )}
              {company.annual_consumption_kwh && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Jahresverbrauch</span>
                  <span className="font-medium">{formatConsumption(company.annual_consumption_kwh)}</span>
                </div>
              )}
              {company.current_price_ct_kwh && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Aktueller Preis</span>
                  <span className="font-medium">{Number(company.current_price_ct_kwh)} ct/kWh</span>
                </div>
              )}
              {company.current_supplier && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Versorger</span>
                  <span className="font-medium">{company.current_supplier}</span>
                </div>
              )}
              {company.annual_consumption_kwh && company.current_price_ct_kwh && (
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className="text-gray-500">Aktuelle Jahreskosten</span>
                  <span className="font-bold text-gray-900">
                    {formatCurrency(company.annual_consumption_kwh * company.current_price_ct_kwh / 100)}
                  </span>
                </div>
              )}
            </div>
          </Card>

          {lead.next_action && (
            <Card className="border-l-4 border-l-amber-400">
              <div className="flex items-center space-x-2 text-amber-700 mb-1">
                <FiClock size={14} />
                <span className="text-xs font-semibold uppercase">Naechste Aktion</span>
              </div>
              <p className="text-sm font-medium text-gray-900">{lead.next_action}</p>
              {lead.next_action_date && (
                <p className="text-xs text-gray-500 mt-1">Faellig: {formatDate(lead.next_action_date)}</p>
              )}
            </Card>
          )}
        </div>

        {/* Rechte Spalte: Tabs */}
        <div className="lg:col-span-2">
          <Card padding={false}>
            <div className="border-b border-gray-200">
              <div className="flex space-x-0">
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? 'border-primary-600 text-primary-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {activeTab === 'uebersicht' && (
                <div className="space-y-4">
                  {/* Schnellinfos */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Erstellt</p>
                      <p className="text-sm font-medium mt-1">{formatDate(lead.created_at)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Aktualisiert</p>
                      <p className="text-sm font-medium mt-1">{formatDate(lead.updated_at)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Lastgang</p>
                      <p className="text-sm font-medium mt-1">
                        {lead.has_load_profile === undefined ? '–' : lead.has_load_profile ? 'Vorhanden' : 'Fehlt'}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Angebote</p>
                      <p className="text-sm font-medium mt-1">{Array.isArray(lead.offers) ? lead.offers.length : '–'}</p>
                    </div>
                  </div>

                  {/* Lastgang Upload */}
                  {!lead.has_load_profile && (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                      <FiUpload className="mx-auto text-gray-400 mb-2" size={24} />
                      <p className="text-sm text-gray-600 font-medium">Lastgang hochladen</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Oder nutzen Sie das <button onClick={() => navigate('/app/lastgang')} className="text-primary-600 hover:underline">Lastgang-Analysetool</button>
                      </p>
                    </div>
                  )}

                  {/* Aktionshistorie Platzhalter */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Verlauf</h4>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 rounded-full bg-primary-500 mt-2" />
                        <div>
                          <p className="text-sm text-gray-700">Lead erstellt</p>
                          <p className="text-xs text-gray-400">{formatDateTime(lead.created_at)}</p>
                        </div>
                      </div>
                      {lead.status !== 'neu' && (
                        <div className="flex items-start space-x-3">
                          <div className="w-2 h-2 rounded-full bg-purple-500 mt-2" />
                          <div>
                            <p className="text-sm text-gray-700">Status geaendert: <StatusBadge status={lead.status} /></p>
                            <p className="text-xs text-gray-400">{formatDateTime(lead.updated_at)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'notizen' && (
                <div>
                  {lead.notes ? (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{lead.notes}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-8">Noch keine Notizen vorhanden.</p>
                  )}
                </div>
              )}

              {activeTab === 'angebote' && (
                <div className="text-center py-8">
                  <FiFileText className="mx-auto text-gray-400 mb-3" size={32} />
                  <p className="text-gray-600 font-medium">Noch keine Angebote erstellt</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Angebote werden im Kalkulator erstellt und per Link versendet.
                  </p>
                  <Button
                    variant="primary"
                    icon={FiDollarSign}
                    className="mt-3"
                    onClick={() => navigate(`/app/kalkulator/lead/${lead.id}`)}
                  >
                    Angebot kalkulieren
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal isOpen={showEditModal} onClose={closeEditModal} title="Lead bearbeiten" size="xl">
        {editError && (
          <div className="flex items-start space-x-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 mb-4">
            <FiAlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
            <p className="text-sm text-red-700">{editError}</p>
          </div>
        )}
        <LeadForm initialData={editInitialData} onSave={handleEdit} onCancel={closeEditModal} />
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={showDeleteConfirm} onClose={closeDeleteConfirm} title="Lead loeschen?" size="sm">
        <p className="text-sm text-gray-600 mb-4">
          Sind Sie sicher, dass Sie den Lead <strong>{company.name}</strong> loeschen moechten?
          Mitgeloescht werden auch die zugehoerigen Portal-Daten: Timeline, hochgeladene
          Dokumente und der Kundenzugang. Diese Aktion kann nicht rueckgaengig gemacht werden.
        </p>
        {deleteError && (
          <div className="flex items-start space-x-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 mb-4">
            <FiAlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
            <div>
              <p className="text-sm font-semibold text-red-700">Lead wurde nicht geloescht</p>
              <p className="text-sm text-red-700/90 mt-0.5">{deleteError}</p>
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3">
          <Button variant="secondary" onClick={closeDeleteConfirm} disabled={deleting}>
            {deleteError ? 'Schliessen' : 'Abbrechen'}
          </Button>
          <Button variant="danger" icon={FiTrash2} onClick={handleDelete} loading={deleting}>
            {deleting ? 'Loesche …' : 'Loeschen'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default LeadDetail;
